import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import type React from 'react';

/** Redirect /broker/deals/:id → /clients/:id (preserves param) */
function RedirectToClient() {
  const { id } = useParams();
  return <Navigate to={`/clients/${id}`} replace />;
}
import { AuthContext, useAuthProvider } from '@/hooks/useAuth';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { ToastProvider } from '@/components/ui/GlassToast';
import { PWAUpdatePrompt } from '@/components/shared/PWAUpdatePrompt';
import { PortalGuard } from '@/components/shared/PortalGuard';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { MasqueradeBanner } from '@/components/shared/MasqueradeBanner';
import { ContactLogPrompt } from '@/components/ui/ContactLogPrompt';

// Auth pages — static imports (always needed immediately)
import LoginPage from '@/routes/auth/login';
import ForgotPasswordPage from '@/routes/auth/forgot-password';
import ResetPasswordPage from '@/routes/auth/reset-password';

// Admin pages
const AdminLayout = lazy(() => import('@/routes/admin/layout'));
const AdminBrokersIndex = lazy(() => import('@/routes/admin/brokers/index'));
const AdminBrokerDetail = lazy(() => import('@/routes/admin/brokers/[slug]'));
const AdminBrokerNew = lazy(() => import('@/routes/admin/brokers/new'));
const AdminLendersIndex = lazy(() => import('@/routes/admin/lenders/index'));
const AdminLenderDetail = lazy(() => import('@/routes/admin/lenders/[slug]'));
const AdminLenderNew = lazy(() => import('@/routes/admin/lenders/new'));
const AdminFieldMap = lazy(() => import('@/routes/admin/lenders/field-map'));
const AdminNotifications = lazy(() => import('@/routes/admin/notifications/index'));
const AdminSettings = lazy(() => import('@/routes/admin/settings/index'));
const AdminNotificationProviders = lazy(() => import('@/routes/admin/settings/providers'));
const AdminAudit = lazy(() => import('@/routes/admin/audit/index'));
const AdminUsersIndex = lazy(() => import('@/routes/admin/users/index'));
const AdminUserNew = lazy(() => import('@/routes/admin/users/new'));
const AdminUserDetail = lazy(() => import('@/routes/admin/users/[id]'));
const AdminDocumentRequirements = lazy(() => import('@/routes/admin/document-requirements'));
const AdminWorkflowRules = lazy(() => import('@/routes/admin/workflow-rules/index'));
const AdminCompliance = lazy(() => import('@/routes/admin/compliance'));
const AdminAnalytics = lazy(() => import('@/routes/admin/analytics'));
const AdminDealQueue = lazy(() => import('@/routes/admin/deal-queue'));
const AdminDealsIndex = lazy(() => import('@/routes/admin/deals/index'));
const AdminDealCommand = lazy(() => import('@/routes/admin/deals/[slug]/command'));
const AdminExtractionRulesIndex = lazy(() => import('@/routes/admin/extraction-rules/index'));
const AdminExtractionRulesNew = lazy(() => import('@/routes/admin/extraction-rules/new'));
const AdminExtractionRuleEdit = lazy(() => import('@/routes/admin/extraction-rules/[ruleId]'));

// Broker pages (kept: layout, commissions, profile, settings, intake)
const BrokerLayout = lazy(() => import('@/routes/broker/layout'));
const BrokerProfile = lazy(() => import('@/routes/broker/profile/index'));
const BrokerWorkflowNew = lazy(() => import('@/routes/broker/workflows/new'));
const BrokerWorkflowEditor = lazy(() => import('@/routes/broker/workflows/[id]'));
const TerrariflowIndex = lazy(() => import('@/routes/admin/workflows/index'));
const TerrariflowV3 = lazy(() => import('@/routes/broker/terrariflow'));
const VaultV3 = lazy(() => import('@/routes/broker/vault'));
const BrokerCommissions = lazy(() => import('@/routes/broker/commissions'));
const BrokerCommissionDetail = lazy(() => import('@/routes/broker/commissions/[id]'));
const BrokerIntegrations = lazy(() => import('@/routes/broker/settings/integrations'));
const BrokerBranding = lazy(() => import('@/routes/broker/settings/branding'));
const BrokerIntake = lazy(() => import('@/routes/broker/intake'));
const BrokerTeam = lazy(() => import('@/routes/broker/settings/team'));
const BrokerWebhooks = lazy(() => import('@/routes/broker/settings/webhooks'));
const BrokerApiKeys = lazy(() => import('@/routes/broker/settings/api-keys'));
const BrokerClientDeals = lazy(() => import('@/routes/broker/clients/[slug]/deals'));
const BrokerClientImport = lazy(() => import('@/routes/broker/clients/import'));
// Pipeline V2 (0120) — broker deal detail, fees, workflow playbooks
const BrokerDealDetail = lazy(() => import('@/routes/broker/deals/[id]/index'));
const BrokerDealFees = lazy(() => import('@/routes/broker/deals/[id]/fees'));
const WorkflowPlaybooksPage = lazy(() => import('@/routes/broker/workflow-playbooks'));
const AdvancedAnalytics = lazy(() => import('@/routes/admin/analytics-advanced'));

// Broker home dashboard (legacy — kept for fallback)
const BrokerHomePage = lazy(() => import('@/routes/home'));
// Cockpit — the new command center HUD (replaces home at /)
const CockpitPage = lazy(() => import('@/routes/cockpit'));
// Cockpit2 — unified deal view scaffold (presentational, SEED-driven)
const Cockpit2Page = lazy(() => import('@/routes/cockpit2'));
const BoardPage = lazy(() => import('@/routes/board'));

// Client detail pages (still accessible from cockpit links)
const UnifiedClientPage = lazy(() => import('@/routes/clients/[id]'));
const ClientDocsPage = lazy(() => import('@/routes/clients/[id]/docs'));
const DealHQPage = lazy(() => import('@/components/deal/DealHQ'));

// Public / unauthenticated pages
const ApplyPage = lazy(() => import('@/routes/apply/[slug]'));
const ApplyChatPage = lazy(() => import('@/routes/apply/chat/[slug]'));
const PublicOffersPage = lazy(() => import('@/routes/public/offers/[token]'));
const PublicIntakeForm = lazy(() => import('@/routes/intake/[brokerSlug]'));
const StartPage = lazy(() => import('@/routes/start/index'));
const ManualPage = lazy(() => import('@/routes/manual/index'));
const UploadPortalPage = lazy(() => import('@/routes/upload/[token]'));
const PortalLinkPage = lazy(() => import('@/routes/portal/[linkId]'));
const Portal2LinkPage = lazy(() => import('@/routes/portal2/[linkId]'));

// Client pages
const ClientLayout = lazy(() => import('@/routes/client/layout'));
const ClientDashboard = lazy(() => import('@/routes/client/dashboard'));
const ClientIntake = lazy(() => import('@/routes/client/intake/index'));
const ClientDocuments = lazy(() => import('@/routes/client/documents/index'));
const ClientDealsIndex = lazy(() => import('@/routes/client/deals/index'));
const ClientDealDetail = lazy(() => import('@/routes/client/deals/[slug]'));
const ClientProfile = lazy(() => import('@/routes/client/profile/index'));
const ClientMagicLink = lazy(() => import('@/routes/client/magic'));
const ClientLoginPage = lazy(() => import('@/routes/client/login'));
const ClientSettings = lazy(() => import('@/routes/client/settings'));

function AuthProvider({ children }: { children: React.ReactNode }) {
  const auth = useAuthProvider();
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

// Invisible fallback — matches app shell background to prevent white flash
const SuspenseFallback = <div className="min-h-screen bg-slate-950" />;

export function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ToastProvider>
          <PWAUpdatePrompt />
          <MasqueradeBanner />
          <ContactLogPrompt />
          <Suspense fallback={SuspenseFallback}>
            <Routes>
              {/* Root — cockpit command center (replaces old home) */}
              <Route path="/" element={<ErrorBoundary><PortalGuard roles={['super_admin', 'broker_admin', 'broker_user', 'employee', 'consultant', 'external_org']}><CockpitPage /></PortalGuard></ErrorBoundary>} />
              {/* Cockpit2 — unified deal view scaffold */}
              <Route path="/cockpit2" element={<ErrorBoundary><PortalGuard roles={['super_admin', 'broker_admin', 'broker_user', 'employee', 'consultant', 'external_org']}><Cockpit2Page /></PortalGuard></ErrorBoundary>} />
              {/* Board — the Waiting Board (home: whose ball + how long) */}
              <Route path="/board" element={<ErrorBoundary><PortalGuard roles={['super_admin', 'broker_admin', 'broker_user', 'employee', 'consultant', 'external_org']}><BoardPage /></PortalGuard></ErrorBoundary>} />
              {/* TEMP dev preview — presentational SEED data only, no auth guard. REMOVE before merge. */}
              <Route path="/cockpit2-preview" element={<ErrorBoundary><Cockpit2Page /></ErrorBoundary>} />
              {/* Legacy home — accessible at /home for transition */}
              <Route path="/home" element={<ErrorBoundary><PortalGuard roles={['super_admin', 'broker_admin', 'broker_user', 'employee', 'consultant', 'external_org']}><BrokerHomePage /></PortalGuard></ErrorBoundary>} />
              {/* Old top-level routes → redirect to cockpit */}
              <Route path="/clients" element={<Navigate to="/" replace />} />
              <Route path="/clients/new" element={<Navigate to="/" replace />} />
              <Route path="/bankers" element={<Navigate to="/" replace />} />
              <Route path="/reports" element={<Navigate to="/" replace />} />
              <Route path="/focus" element={<Navigate to="/" replace />} />
              <Route path="/applications" element={<Navigate to="/" replace />} />
              {/* Client detail + docs still accessible (cockpit links to them) */}
              <Route path="/clients/:id" element={<ErrorBoundary><PortalGuard roles={['super_admin', 'broker_admin', 'broker_user', 'employee', 'consultant', 'external_org']}><UnifiedClientPage /></PortalGuard></ErrorBoundary>} />
              <Route path="/clients/:id/docs" element={<ErrorBoundary><PortalGuard roles={['super_admin', 'broker_admin', 'broker_user', 'employee', 'consultant']}><ClientDocsPage /></PortalGuard></ErrorBoundary>} />
              <Route path="/deals/:dealId/hq" element={<ErrorBoundary><PortalGuard roles={['super_admin', 'broker_admin', 'broker_user', 'employee', 'consultant']}><DealHQPage /></PortalGuard></ErrorBoundary>} />
              <Route path="/intake" element={<ErrorBoundary><PortalGuard roles={['super_admin', 'broker_admin', 'broker_user', 'employee', 'consultant', 'external_org']}><BrokerIntake /></PortalGuard></ErrorBoundary>} />

              {/* Auth routes */}
              <Route path="/login" element={<ErrorBoundary><LoginPage /></ErrorBoundary>} />
              <Route path="/forgot-password" element={<ErrorBoundary><ForgotPasswordPage /></ErrorBoundary>} />
              <Route path="/reset" element={<ErrorBoundary><ResetPasswordPage /></ErrorBoundary>} />
              <Route path="/reset-password" element={<ErrorBoundary><ResetPasswordPage /></ErrorBoundary>} />

              {/* Admin routes */}
              <Route path="/admin" element={<ErrorBoundary><PortalGuard roles={['super_admin']}><AdminLayout /></PortalGuard></ErrorBoundary>}>
                <Route index element={<Navigate to="/" replace />} />
                <Route path="dashboard" element={<Navigate to="/" replace />} />
                <Route path="brokers" element={<AdminBrokersIndex />} />
                <Route path="brokers/new" element={<AdminBrokerNew />} />
                <Route path="brokers/:slug" element={<AdminBrokerDetail />} />
                <Route path="lenders" element={<AdminLendersIndex />} />
                <Route path="lenders/new" element={<AdminLenderNew />} />
                <Route path="lenders/:slug" element={<AdminLenderDetail />} />
                <Route path="lenders/:slug/field-map" element={<AdminFieldMap />} />
                <Route path="clients" element={<Navigate to="/clients" replace />} />
                <Route path="users" element={<AdminUsersIndex />} />
                <Route path="users/new" element={<AdminUserNew />} />
                <Route path="users/:id" element={<AdminUserDetail />} />
                <Route path="notifications" element={<AdminNotifications />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="settings/providers" element={<AdminNotificationProviders />} />
                <Route path="workflows" element={<TerrariflowIndex />} />
                <Route path="workflows/new" element={<BrokerWorkflowNew />} />
                <Route path="workflows/:id" element={<BrokerWorkflowEditor />} />
                <Route path="analytics" element={<AdminAnalytics />} />
                <Route path="analytics/advanced" element={<AdvancedAnalytics />} />
                <Route path="audit" element={<AdminAudit />} />
                <Route path="document-requirements" element={<AdminDocumentRequirements />} />
                <Route path="workflow-rules" element={<AdminWorkflowRules />} />
                <Route path="compliance" element={<AdminCompliance />} />
                <Route path="deal-queue" element={<AdminDealQueue />} />
                <Route path="deals" element={<AdminDealsIndex />} />
                <Route path="deals/:slug/command" element={<AdminDealCommand />} />
                <Route path="extraction-rules" element={<AdminExtractionRulesIndex />} />
                <Route path="extraction-rules/new" element={<AdminExtractionRulesNew />} />
                <Route path="extraction-rules/:ruleId" element={<AdminExtractionRuleEdit />} />
              </Route>

              {/* Broker routes (remaining: commissions, settings, intake, profile) */}
              <Route path="/broker" element={<ErrorBoundary><PortalGuard roles={['super_admin', 'broker_admin', 'broker_user', 'employee', 'consultant', 'external_org']}><BrokerLayout /></PortalGuard></ErrorBoundary>}>
                <Route index element={<Navigate to="/" replace />} />
                <Route path="dashboard" element={<Navigate to="/" replace />} />
                <Route path="commissions" element={<BrokerCommissions />} />
                <Route path="commissions/:id" element={<BrokerCommissionDetail />} />
                <Route path="settings" element={<BrokerIntegrations />} />
                <Route path="settings/integrations" element={<BrokerIntegrations />} />
                <Route path="settings/branding" element={<BrokerBranding />} />
                <Route path="settings/team" element={<BrokerTeam />} />
                <Route path="settings/webhooks" element={<BrokerWebhooks />} />
                <Route path="settings/api-keys" element={<BrokerApiKeys />} />
                <Route path="intake" element={<Navigate to="/intake" replace />} />
                <Route path="profile" element={<BrokerProfile />} />
                {/* Pipeline V2 (0120) — broker deal detail page replaces the
                    previous redirect to /clients/:id. The fees page is a
                    standalone full-page route linked from the deal header. */}
                <Route path="deals/:id" element={<BrokerDealDetail />} />
                <Route path="deals/:id/fees" element={<BrokerDealFees />} />
                <Route path="workflow-playbooks" element={<WorkflowPlaybooksPage />} />
                <Route path="terrariflow" element={<TerrariflowV3 />} />
                <Route path="vault" element={<VaultV3 />} />
                {/* Redirects for dead routes merged into unified pages */}
                <Route path="deals" element={<Navigate to="/" replace />} />
                <Route path="clients" element={<Navigate to="/clients" replace />} />
                <Route path="clients/import" element={<BrokerClientImport />} />
                <Route path="clients/:slug/deals" element={<BrokerClientDeals />} />
                <Route path="clients/:id" element={<RedirectToClient />} />
                <Route path="tasks" element={<Navigate to="/" replace />} />
                <Route path="money-tasks" element={<Navigate to="/" replace />} />
              </Route>

              {/* Client routes */}
              <Route path="/client" element={<ErrorBoundary><PortalGuard roles={['super_admin', 'client']}><ClientLayout /></PortalGuard></ErrorBoundary>}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<ClientDashboard />} />
                <Route path="intake" element={<ClientIntake />} />
                <Route path="documents" element={<ClientDocuments />} />
                <Route path="deals" element={<ClientDealsIndex />} />
                <Route path="deals/:slug" element={<ClientDealDetail />} />
                <Route path="profile" element={<ClientProfile />} />
                <Route path="settings" element={<ClientSettings />} />
              </Route>

              {/* Client magic link login (unauthenticated) */}
              <Route path="/client/magic/:token" element={<ErrorBoundary><ClientMagicLink /></ErrorBoundary>} />
              <Route path="/client-login" element={<ErrorBoundary><ClientLoginPage /></ErrorBoundary>} />

              {/* Public routes (unauthenticated) */}
              <Route path="/apply/:slug" element={<ErrorBoundary><ApplyPage /></ErrorBoundary>} />
              <Route path="/apply/:slug/chat" element={<ErrorBoundary><ApplyChatPage /></ErrorBoundary>} />
              <Route path="/offers/:token" element={<ErrorBoundary><PublicOffersPage /></ErrorBoundary>} />
              <Route path="/intake/:brokerSlug" element={<ErrorBoundary><PublicIntakeForm /></ErrorBoundary>} />
              {/* Public front door (mobile-first, AllClear look) */}
              <Route path="/start" element={<ErrorBoundary><StartPage /></ErrorBoundary>} />
              <Route path="/start/:brokerSlug" element={<ErrorBoundary><StartPage /></ErrorBoundary>} />
              <Route path="/upload/:token" element={<ErrorBoundary><UploadPortalPage /></ErrorBoundary>} />
              {/* Cutover 2026-07-01: /portal (real client links) now renders the v2 redesign.
                  Old PortalLinkPage kept lazy-imported below for instant rollback (swap the element). */}
              <Route path="/portal/:linkId" element={<ErrorBoundary><Portal2LinkPage /></ErrorBoundary>} />
              <Route path="/portal2/:linkId" element={<ErrorBoundary><Portal2LinkPage /></ErrorBoundary>} />
              <Route path="/portal-legacy/:linkId" element={<ErrorBoundary><PortalLinkPage /></ErrorBoundary>} />
              <Route path="/manual" element={<ErrorBoundary><ManualPage /></ErrorBoundary>} />

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </Suspense>
        </ToastProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
